'use client';

import type {
  MysteryRoomDoorDefinition,
  MysteryRoomHazardDefinition,
  MysteryRoomIncidentAnchorDefinition,
  MysteryRoomInteractableDefinition,
  MysteryRoomLightDefinition,
  MysteryRoomMapDefinition,
  MysteryRoomNavEdgeDefinition,
  MysteryRoomNavNodeDefinition,
  MysteryRoomOccluderDefinition,
  MysteryRoomPatrolNode,
  MysteryRoomPatrolRouteDefinition,
  MysteryRoomPropDefinition,
  MysteryRoomRect,
  MysteryRoomSegment,
  MysteryRoomSpawnSlotDefinition,
  MysteryRoomCustomObjectDefinition,
  MysteryRoomTriggerDefinition,
  MysteryRoomSoundSourceDefinition,
  MysteryRoomVec2,
  MysteryRoomWorkAnchorDefinition,
  MysteryRoomZoneDefinition,
} from './mapTypes';

export function scaleMysteryRoomMap(map: MysteryRoomMapDefinition, scale: number): MysteryRoomMapDefinition {
  if (scale === 1) {
    return map;
  }

  return {
    ...map,
    width: scaleNumber(map.width, scale),
    height: scaleNumber(map.height, scale),
    spawn: scalePoint(map.spawn, scale),
    spawnSlots: (map.spawnSlots ?? [{ x: map.spawn.x, y: map.spawn.y, room: map.spawnZoneId, zoneId: map.spawnZoneId }]).map((slot) => scaleSpawnSlot(slot, scale)),
    visibilityRadius: scaleNumber(map.visibilityRadius, scale),
    rooms: map.rooms.map((room) => scaleRoom(room, scale)),
    doors: map.doors.map((door) => scaleDoor(door, scale)),
    walls: map.walls.map((segment) => scaleSegment(segment, scale)),
    occluders: map.occluders.map((occluder) => scaleOccluder(occluder, scale)),
    interactables: map.interactables.map((interactable) => scaleInteractable(interactable, scale)),
    workAnchors: map.workAnchors.map((anchor) => scaleWorkAnchor(anchor, scale)),
    navNodes: map.navNodes.map((node) => scaleNavNode(node, scale)),
    navEdges: map.navEdges.map((edge) => scaleNavEdge(edge, scale)),
    incidentAnchors: map.incidentAnchors.map((anchor) => scaleIncidentAnchor(anchor, scale)),
    patrolRoutes: map.patrolRoutes.map((route) => scalePatrolRoute(route, scale)),
    npcs: map.npcs.map((npc) => ({
      ...npc,
      spawn: scalePoint(npc.spawn, scale),
    })),
    lights: map.lights.map((light) => scaleLight(light, scale)),
    hazards: map.hazards.map((hazard) => scaleHazard(hazard, scale)),
    props: map.props.map((prop) => scaleProp(prop, scale)),
    customObjects: (map.customObjects ?? []).map((object) => scaleCustomObject(object, scale)),
    triggers: (map.triggers ?? []).map((trigger) => scaleTrigger(trigger, scale)),
    soundDefinitions: [...(map.soundDefinitions ?? [])],
    soundSources: (map.soundSources ?? []).map((source) => scaleSoundSource(source, scale)),
  };
}

function scaleSpawnSlot(slot: MysteryRoomSpawnSlotDefinition, scale: number): MysteryRoomSpawnSlotDefinition {
  return {
    ...slot,
    x: scaleNumber(slot.x, scale),
    y: scaleNumber(slot.y, scale),
  };
}

function scaleRoom(room: MysteryRoomZoneDefinition, scale: number): MysteryRoomZoneDefinition {
  return {
    ...room,
    floor: room.floor.map((floor) => scaleRect(floor, scale)),
    labelPoint: scalePoint(room.labelPoint, scale),
  };
}

function scaleDoor(door: MysteryRoomDoorDefinition, scale: number): MysteryRoomDoorDefinition {
  return {
    ...door,
    panel: scaleRect(door.panel, scale),
    trigger: scaleRect(door.trigger, scale),
  };
}

function scaleOccluder(occluder: MysteryRoomOccluderDefinition, scale: number): MysteryRoomOccluderDefinition {
  return {
    ...occluder,
    rect: scaleRect(occluder.rect, scale),
  };
}

function scaleInteractable(
  interactable: MysteryRoomInteractableDefinition,
  scale: number,
): MysteryRoomInteractableDefinition {
  return {
    ...interactable,
    anchor: scalePoint(interactable.anchor, scale),
    interactionRadius: scaleNumber(interactable.interactionRadius, scale),
  };
}

function scaleWorkAnchor(anchor: MysteryRoomWorkAnchorDefinition, scale: number): MysteryRoomWorkAnchorDefinition {
  return {
    ...anchor,
    position: scalePoint(anchor.position, scale),
    taskAreaRadius: typeof anchor.taskAreaRadius === 'number' ? scaleNumber(anchor.taskAreaRadius, scale) : anchor.taskAreaRadius,
  };
}

function scaleNavNode(node: MysteryRoomNavNodeDefinition, scale: number): MysteryRoomNavNodeDefinition {
  return {
    ...node,
    position: scalePoint(node.position, scale),
  };
}

function scaleNavEdge(edge: MysteryRoomNavEdgeDefinition, scale: number): MysteryRoomNavEdgeDefinition {
  return {
    ...edge,
    distanceCost: scaleNumber(edge.distanceCost, scale),
  };
}

function scaleIncidentAnchor(
  anchor: MysteryRoomIncidentAnchorDefinition,
  scale: number,
): MysteryRoomIncidentAnchorDefinition {
  return {
    ...anchor,
    position: scalePoint(anchor.position, scale),
  };
}

function scalePatrolRoute(route: MysteryRoomPatrolRouteDefinition, scale: number): MysteryRoomPatrolRouteDefinition {
  return {
    ...route,
    nodes: route.nodes.map((node) => scalePatrolNode(node, scale)),
  };
}

function scalePatrolNode(node: MysteryRoomPatrolNode, scale: number): MysteryRoomPatrolNode {
  return {
    ...node,
    x: scaleNumber(node.x, scale),
    y: scaleNumber(node.y, scale),
  };
}

function scaleLight(light: MysteryRoomLightDefinition, scale: number): MysteryRoomLightDefinition {
  return {
    ...light,
    position: scalePoint(light.position, scale),
    radius: scaleNumber(light.radius, scale),
  };
}

function scaleHazard(hazard: MysteryRoomHazardDefinition, scale: number): MysteryRoomHazardDefinition {
  return {
    ...hazard,
    position: scalePoint(hazard.position, scale),
    radius: scaleNumber(hazard.radius, scale),
  };
}

function scaleProp(prop: MysteryRoomPropDefinition, scale: number): MysteryRoomPropDefinition {
  return {
    ...prop,
    rect: scaleRect(prop.rect, scale),
  };
}

function scaleCustomObject(object: MysteryRoomCustomObjectDefinition, scale: number): MysteryRoomCustomObjectDefinition {
  const collision =
    object.collision.kind === 'rect'
      ? { ...object.collision, rect: scaleRect(object.collision.rect, scale) }
      : object.collision.kind === 'circle'
        ? {
            ...object.collision,
            center: scalePoint(object.collision.center, scale),
            radius: scaleNumber(object.collision.radius, scale),
          }
        : object.collision.kind === 'polygon'
          ? { ...object.collision, points: object.collision.points.map((point) => scalePoint(point, scale)) }
          : object.collision;

  return {
    ...object,
    rect: scaleRect(object.rect, scale),
    assetTransform: object.assetTransform
      ? {
          ...object.assetTransform,
          offsetX: typeof object.assetTransform.offsetX === 'number' ? scaleNumber(object.assetTransform.offsetX, scale) : object.assetTransform.offsetX,
          offsetY: typeof object.assetTransform.offsetY === 'number' ? scaleNumber(object.assetTransform.offsetY, scale) : object.assetTransform.offsetY,
        }
      : object.assetTransform,
    collision,
  };
}

function scaleTrigger(trigger: MysteryRoomTriggerDefinition, scale: number): MysteryRoomTriggerDefinition {
  return {
    ...trigger,
    rect: scaleRect(trigger.rect, scale),
    actions: trigger.actions.map((action) => {
      if ((action.kind === 'spawn_smoke' || action.kind === 'explosion') && action.position) {
        return {
          ...action,
          position: scalePoint(action.position, scale),
          radius: typeof action.radius === 'number' ? scaleNumber(action.radius, scale) : action.radius,
        };
      }
      return action;
    }),
  };
}

function scaleSoundSource(source: MysteryRoomSoundSourceDefinition, scale: number): MysteryRoomSoundSourceDefinition {
  return {
    ...source,
    position: scalePoint(source.position, scale),
    radius: scaleNumber(source.radius, scale),
  };
}

function scaleSegment(segment: MysteryRoomSegment, scale: number): MysteryRoomSegment {
  return {
    ...segment,
    start: scalePoint(segment.start, scale),
    end: scalePoint(segment.end, scale),
  };
}

function scaleRect(rect: MysteryRoomRect, scale: number): MysteryRoomRect {
  return {
    x: scaleNumber(rect.x, scale),
    y: scaleNumber(rect.y, scale),
    width: scaleNumber(rect.width, scale),
    height: scaleNumber(rect.height, scale),
  };
}

function scalePoint(point: MysteryRoomVec2, scale: number): MysteryRoomVec2 {
  return {
    x: scaleNumber(point.x, scale),
    y: scaleNumber(point.y, scale),
  };
}

function scaleNumber(value: number, scale: number) {
  return Math.round(value * scale * 1000) / 1000;
}
